import { Alert, AlertTitle } from "@mui/material";

/** A model that failed to load, keyed by its config id. */
export interface ModelLoadFailure {
  modelId: string;
  message: string;
}

interface Props {
  failures: ModelLoadFailure[];
  onDismiss: () => void;
  className?: string;
}

/**
 * Reports models that could not be loaded.
 *
 * A model load failure is recoverable and specific — a missing external
 * buffer, an unparseable file — so it is surfaced here rather than through
 * `RenderingError`, which is reserved for the unrecoverable case (no WebGL)
 * that takes the whole panel down. The canvas keeps rendering everything
 * that did load.
 *
 * Messages come from `ModelLoader`, which names the offending file and, where
 * it can, the specific asset that is missing.
 */
export function ModelLoadErrorAlert({ failures, onDismiss, className }: Props) {
  if (failures.length === 0) {
    return null;
  }

  return (
    <Alert severity="error" onClose={onDismiss} className={className}>
      <AlertTitle>
        {failures.length === 1
          ? "A model could not be loaded"
          : `${failures.length} models could not be loaded`}
      </AlertTitle>
      {failures.map((failure) => (
        <div key={failure.modelId}>{failure.message}</div>
      ))}
    </Alert>
  );
}
